import React, { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faFilter, faTags, faThLarge, faBox } from "@fortawesome/free-solid-svg-icons";

// Mobile-first responsive component for Saksham SHG Products
// Dark theme: black + pink accents. Tailwind required in the project.

const PRODUCTS = [
  { id: 1, category: "Traditional Food & Snacks", product: "Tilkut / Til Patti / Til Ladoo", brand: "BABITA", positioning: "Heritage sweet, festival & gifting friendly, premium packaging" },
  { id: 2, category: "Traditional Food & Snacks", product: "Papad / Khakhra / Fryums", brand: "VIBHA", positioning: "Everyday household staple, hygienic & consistent quality" },
  { id: 3, category: "Traditional Food & Snacks", product: "Pickles (Mango, Lemon, Chili, Garlic)", brand: "KHUSHI", positioning: "Tangy, spicy, urban & diaspora appeal" },
  { id: 4, category: "Traditional Food & Snacks", product: "Ready Mixes (Dosa, Idli, Chilla, Millet Mixes)", brand: "RITU", positioning: "Healthy, urban-ready, convenient, modern packaging" },
  { id: 5, category: "Traditional Food & Snacks", product: "Regional Sweets (Chikki, Murabba, Peda, Ladoo)", brand: "BABITA", positioning: "Cultural & festival focus, giftable, artisanal story" },
  { id: 6, category: "Traditional Food & Snacks", product: "Roasted Snacks (Gram, Groundnuts, Foxnuts / Makhana)", brand: "KHUSHI", positioning: "Healthy, snackable, protein-rich, gift/party packs" },
  { id: 7, category: "Natural & Organic Products", product: "Cold-pressed Oils (Sesame, Mustard, Coconut)", brand: "RITU", positioning: "Health & wellness positioning, premium label" },
  { id: 8, category: "Natural & Organic Products", product: "Honey & Bee Products", brand: "KHUSHI", positioning: "Pure, traceable, artisanal, urban & giftable" },
  { id: 9, category: "Natural & Organic Products", product: "Millet & Grain Products (Flours, Cookies, Health Mixes)", brand: "RITU", positioning: "Health-conscious, urban market, aligned with millet promotion" },
  { id: 10, category: "Natural & Organic Products", product: "Herbal Powders & Teas (Tulsi, Amla, Moringa, Lemongrass)", brand: "KHUSHI", positioning: "Ayurvedic wellness, herbal, eco-friendly packaging" },
  { id: 11, category: "Handcrafted & Wellness Products", product: "Natural Soaps & Herbal Shampoo Bars", brand: "VIBHA", positioning: "Sustainable, everyday self-care, rural women empowerment story" },
  { id: 12, category: "Handcrafted & Wellness Products", product: "Agarbatti / Dhoop / Herbal Cones", brand: "VIBHA", positioning: "Spiritual / aromatic products, daily use, eco-friendly" },
  { id: 13, category: "Handcrafted & Wellness Products", product: "Handmade Candles / Aroma Products", brand: "VIBHA", positioning: "Decorative, artisanal, giftable, festival-friendly" },
  { id: 14, category: "Handcrafted & Wellness Products", product: "Natural Cleaners (Neem, Lemon, Bio-enzymes)", brand: "KHUSHI", positioning: "Eco-friendly, household segment, urban conscious consumers" },
  { id: 15, category: "Handicraft & Textile Based", product: "Jute / Bamboo / Cane Craft", brand: "BABITA", positioning: "Home décor, utility, eco-conscious gift market" },
  { id: 16, category: "Handicraft & Textile Based", product: "Handloom / Khadi Products", brand: "RITU", positioning: "Apparel, scarves, home linen; urban + export potential" },
  { id: 17, category: "Handicraft & Textile Based", product: "Terracotta / Clay Ware / Pottery", brand: "BABITA", positioning: "Regional artisanal heritage products" },
  { id: 18, category: "Toys / Playthings", product: "Handmade Dolls / Cloth / Wooden Toys", brand: "GUDIA", positioning: "Safe, eco-friendly, culturally inspired toys; storytelling focus" },
  { id: 19, category: "Toys / Playthings", product: "Educational Toys (Wooden puzzles, abacus, bead sets)", brand: "GUDIA", positioning: "Learning through play; rural artisan craft meets urban design" },
  { id: 20, category: "Agro-based & Allied Products", product: "Desi Cow Ghee / Panchagavya Products", brand: "RITU", positioning: "Ayurvedic, premium food & wellness segment" },
  { id: 21, category: "Agro-based & Allied Products", product: "Processed Pulses & Spices", brand: "KHUSHI", positioning: "Packaged, hygienic, urban-ready, traceable" },
  { id: 22, category: "Agro-based & Allied Products", product: "Organic Vermicompost / Biofertilizers", brand: "VIBHA", positioning: "Sustainable farming, eco-conscious buyers" }
];

const BRANDS = ["ALL", "VIBHA", "RITU", "BABITA", "KHUSHI", "GUDIA"];

export default function SakshamSHGProducts() {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [brand, setBrand] = useState("ALL");
  const [showOnlyCategory, setShowOnlyCategory] = useState("");

  const categories = useMemo(() => {
    const set = new Set(PRODUCTS.map(p => p.category));
    return Array.from(set);
  }, []);

  const filtered = useMemo(() => {
    return PRODUCTS.filter(p => {
      const q = query.trim().toLowerCase();
      if (brand !== "ALL" && p.brand !== brand) return false;
      if (showOnlyCategory && p.category !== showOnlyCategory) return false;
      if (!q) return true;
      return (
        p.product.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.brand.toLowerCase().includes(q) ||
        p.positioning.toLowerCase().includes(q)
      );
    });
  }, [query, brand, showOnlyCategory]);

  return (
    <div className="min-h-screen flex flex-col bg-[#1a0b1e] text-pink-100 font-sans">
      <header className="sticky top-0 z-50 bg-[#2a0f2f] border-b border-pink-800 px-4 py-2 flex items-center justify-between shadow-lg">
        <div className="flex flex-col">
          <div className="text-xl lg:text-2xl font-bold text-pink-400">🌟 SAKSHAM SHG</div>
          <div className="text-[10px] lg:text-lg text-yellow-200">
            SAKSHAM: Skilled And Keen, Systematic Homes Achieves More!
          </div>
        </div>

        {/* Desktop user controls */}
        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs text-left text-pink-300">{userName}</div>
              <button
                onClick={() => { dispatch(logout()); navigate("/sheearns"); }}
                className="p-2 mt-2 w-8 rounded-md bg-rose-600 hover:bg-rose-700 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-3 py-1 rounded-full border border-pink-700 text-sm text-pink-200 hover:bg-pink-900"
              >
                Login
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-3 py-1 rounded-full bg-pink-500 text-white text-sm hover:bg-pink-400"
              >
                Register
              </button>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-6 h-6 flex items-center justify-center text-xl text-pink-300 bg-pink-500"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {/* search & filters */}
      <div className="mt-4 grid grid-cols-1 gap-3">
          <div className="relative">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products, category, brand or positioning..."
              className="w-full rounded-xl bg-pink-900/10 placeholder-pink-200/60 border border-pink-800/30 px-12 py-3 text-pink-50 focus:outline-none focus:ring-2 focus:ring-pink-600"
            />
            <span className="absolute left-3 w-8 top-3 text-pink-300">
              <FontAwesomeIcon icon={faSearch} size="sm" />
            </span>
          </div>

          <div className="grid grid-cols-6 gap-2 py-1 place-items-center">
            {BRANDS.map(b => (
              <button
                key={b}
                onClick={() => { setBrand(b); setShowOnlyCategory(""); }}
                className={`w-full px-1 py-2 rounded-md text-sm font-medium ${brand === b ? 'bg-pink-600 text-black' : 'bg-pink-900/20 text-pink-200'}`}>
                {b}
              </button>
            ))}
          </div>

          <div className="flex gap-2 items-center">
            <select
              value={showOnlyCategory}
              onChange={(e) => setShowOnlyCategory(e.target.value)}
              className="rounded-lg bg-pink-900/10 px-3 py-2 h-10 text-pink-100">
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <button onClick={() => { setQuery(''); setBrand('ALL'); setShowOnlyCategory(''); }} className="ml-auto px-3 py-2 rounded-lg bg-transparent border border-pink-800 text-pink-200">Reset</button>
          </div>
        </div>      
    
      <main className="mx-auto mt-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${query}-${brand}-${showOnlyCategory}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
          >
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map(item => (
                <motion.article
                  key={item.id}
                  layout
                  whileHover={{ scale: 1.02 }}
                  className="rounded-2xl p-4 bg-gradient-to-br from-pink-900/10 to-transparent border border-pink-800/30 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-14 h-14 rounded-lg bg-pink-800/30 flex items-center justify-center text-pink-100 text-lg font-bold">{item.brand[0]}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-pink-100">{item.product}</h3>
                        <span className="text-xs px-2 py-1 rounded-full bg-pink-800/20 text-pink-200">{item.brand}</span>
                      </div>
                      <p className="mt-2 text-xs text-pink-200/80">{item.positioning}</p>
                      <div className="mt-3 flex items-center gap-2">
                        <button className="text-xs px-3 py-1 rounded-lg bg-pink-600 text-black">View</button>
                        <button className="text-xs px-3 py-1 rounded-lg bg-transparent border border-pink-800 text-pink-200">Edit</button>
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}

              {filtered.length === 0 && (
                <div className="col-span-full rounded-2xl p-6 bg-pink-900/10 border border-pink-800/20 text-center">
                  <p className="text-pink-200">No products match your filters. Try a different search.</p>
                </div>
              )}
            </section>
          </motion.div>
        </AnimatePresence>

        <footer className="bg-[#2a0f2f] border-t border-pink-800 text-pink-300 py-4">
          <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-xs lg:text-sm">
              © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
            </div>
            <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
              <Link to="/about-us" className="text-white hover:text-pink-400">About</Link>
              <Link to="/privacy-policy" className="text-white hover:text-pink-400">Privacy Policy</Link>
              <Link to="/terms-of-use" className="text-white hover:text-pink-400">Terms of Use</Link>
              <Link to="/contact-us" className="text-white hover:text-pink-400">Contact</Link>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}